import { useTheme } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { XAxis, YAxis, ReferenceLine } from "recharts";
import { formatYAxisTickLabel } from "../utils";
import { GRID_LINE_HEIGHT, PADDING_BOTTOM_Y_AXIS } from "../LineChart";

const DEFAULT_MIN_Y_AXIS_VALUE = 5;
const AXIS_TICK_FONT_SIZE = 10;
const AXIS_TICK_FONT_WEIGHT = 400;
const HIDDEN_TICK_FONT_SIZE = 0;
const X_AXIS_HEIGHT = 40;
const MIN_LABEL_SPACING = 60;

// Matches a year (e.g. "2025") that appears on its own line in a formatted label,
// as produced by chart data utilities ("Jan\n2025"). Used to track year boundaries.
const YEAR_ON_NEW_LINE_REGEX = /\n((19|20)\d{2})/;
const YEAR_LINE_STRIP_REGEX = /\n(19|20)\d{2}/g;

function buildVisibleIndices(
  containerWidth: number,
  xData: string[],
): Set<number> | null {
  if (containerWidth === 0 || xData.length === 0) return null;

  const maxVisible = Math.max(
    1,
    Math.floor(containerWidth / MIN_LABEL_SPACING),
  );

  if (xData.length <= maxVisible) return null;

  const step = Math.ceil(xData.length / maxVisible);
  const indices = new Set<number>();

  for (let i = 0; i < xData.length; i++) {
    if (i % step === 0) indices.add(i);
  }

  return indices;
}

function buildHideYearIndices(
  xData: string[],
  visibleIndices: Set<number> | null,
  xTickFormatter?: (v: string) => string,
): Set<number> {
  const hideIndices = new Set<number>();
  const shownYears = new Set<string>();

  for (let i = 0; i < xData.length; i++) {
    const isVisible = !visibleIndices || visibleIndices.has(i);

    if (!isVisible) continue;

    const displayValue = xTickFormatter
      ? xTickFormatter(xData[i] ?? "")
      : (xData[i] ?? "");
    const year = YEAR_ON_NEW_LINE_REGEX.exec(displayValue)?.[1] ?? "";

    if (!year) continue;

    if (shownYears.has(year)) {
      hideIndices.add(i);
    } else {
      shownYears.add(year);
    }
  }

  return hideIndices;
}

interface LineChartAxesProps<T> {
  isMobile: boolean;
  xKey: string & keyof T;
  xTickFormatter?: (value: string) => string;
  yMax: number;
  yTicks: number[];
  minYAxisValue?: number;
  containerWidth: number;
  xData: string[];
}

interface MultilineXAxisTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: string };
  fill?: string;
  formatter?: (value: string) => string;
  index?: number;
  visibleIndices?: Set<number>;
  hideYearIndices?: Set<number>;
}

const MultilineXAxisTick: React.FC<MultilineXAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  fill = "currentColor",
  formatter,
  index,
  visibleIndices,
  hideYearIndices,
}) => {
  if (!payload?.value) return null;

  if (visibleIndices && index !== undefined && !visibleIndices.has(index)) {
    return null;
  }

  const rawDisplay = formatter ? formatter(payload.value) : payload.value;
  // Strip the year line (e.g. "\n2025") when this year was already shown by
  // an earlier visible tick; otherwise keep it as-is.
  const displayValue =
    index !== undefined && hideYearIndices?.has(index)
      ? rawDisplay.replaceAll(YEAR_LINE_STRIP_REGEX, "")
      : rawDisplay;
  const lines = displayValue.split("\n");

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        fill={fill}
        fontSize={AXIS_TICK_FONT_SIZE}
        fontWeight={AXIS_TICK_FONT_WEIGHT}
        textAnchor="middle"
      >
        {lines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tspan key={`${line}-${index}`} x={0} y={12 + index * 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export const LineChartAxes = <T,>({
  isMobile,
  xKey,
  xTickFormatter,
  yMax,
  yTicks,
  minYAxisValue = DEFAULT_MIN_Y_AXIS_VALUE,
  containerWidth,
  xData,
}: LineChartAxesProps<T>): React.ReactElement => {
  const theme = useTheme();

  const formatYAxisTick = useCallback(
    (value: number) => {
      return formatYAxisTickLabel(value, minYAxisValue, yMax, minYAxisValue);
    },
    [yMax, minYAxisValue],
  );

  const { visibleIndices, hideYearIndices } = useMemo(() => {
    const computedVisibleIndices = buildVisibleIndices(containerWidth, xData);
    const computedHideYearIndices = buildHideYearIndices(
      xData,
      computedVisibleIndices,
      xTickFormatter,
    );

    return {
      visibleIndices: computedVisibleIndices,
      hideYearIndices: computedHideYearIndices,
    };
  }, [containerWidth, xData, xTickFormatter]);

  const xAxisTick = isMobile
    ? { fontSize: HIDDEN_TICK_FONT_SIZE }
    : (props: MultilineXAxisTickProps) => (
        <MultilineXAxisTick
          {...props}
          fill={theme.colors.text[1]}
          formatter={xTickFormatter}
          hideYearIndices={hideYearIndices}
          visibleIndices={visibleIndices ?? undefined}
        />
      );

  return (
    <>
      <XAxis
        axisLine={{
          stroke: theme.colors.defaultBackground,
          strokeWidth: PADDING_BOTTOM_Y_AXIS,
        }}
        dataKey={String(xKey)}
        height={isMobile ? 8 : X_AXIS_HEIGHT}
        interval={0}
        tick={xAxisTick}
        tickLine={false}
        tickMargin={-PADDING_BOTTOM_Y_AXIS}
      />
      <YAxis
        allowDataOverflow
        axisLine={{
          stroke: theme.colors.secondary[3],
          strokeDasharray: `${GRID_LINE_HEIGHT} ${PADDING_BOTTOM_Y_AXIS}`,
        }}
        domain={[minYAxisValue, yMax]}
        padding={{ bottom: 4 }}
        tick={
          isMobile
            ? { fontSize: HIDDEN_TICK_FONT_SIZE }
            : {
                fill: theme.colors.text[1],
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
              }
        }
        tickFormatter={formatYAxisTick}
        tickLine={false}
        ticks={yTicks}
        width={8}
      />
      <ReferenceLine stroke={theme.colors.secondary[3]} y={minYAxisValue} />
    </>
  );
};
